/**
 * First-win activation kit (Conversion Program).
 *
 * After express register / guest alert succeed, land people on one primary next
 * action toward a durable 7d win. Reserved door ids support later claim / Write
 * for APT without renaming the contract.
 */

import {
  EXPRESS_REGISTER_SUCCESS_ALERTS_CTA,
  EXPRESS_REGISTER_SUCCESS_BROWSE_CTA,
  EXPRESS_REGISTER_SUCCESS_CLAIM_CTA,
} from './expressRegisterCopy.js';

export const FIRST_WIN_DOOR_EXPRESS_REGISTER = 'express_register';
export const FIRST_WIN_DOOR_GUEST_ALERT = 'guest_alert';
export const FIRST_WIN_DOOR_PEOPLE_CLAIM = 'people_claim';
export const FIRST_WIN_DOOR_WRITE_FOR_APT = 'write_for_apt';

export const FIRST_WIN_DOORS = [
  FIRST_WIN_DOOR_EXPRESS_REGISTER,
  FIRST_WIN_DOOR_GUEST_ALERT,
  FIRST_WIN_DOOR_PEOPLE_CLAIM,
  FIRST_WIN_DOOR_WRITE_FOR_APT,
] as const;

export type FirstWinDoor = (typeof FIRST_WIN_DOORS)[number];

/** Reminder eligible when door complete is between these ages (hours). */
export const FIRST_WIN_REMINDER_MIN_AGE_HOURS = 24;
export const FIRST_WIN_REMINDER_MAX_AGE_HOURS = 36;

/** Express claim win must land within this many days of the express booking. */
export const FIRST_WIN_EXPRESS_CLAIM_WINDOW_DAYS = 7;

export const FIRST_WIN_EXPRESS_DAY0_CAMPAIGN = 'first_win_express_claim_day0';
export const FIRST_WIN_EXPRESS_REMINDER_CAMPAIGN = 'first_win_express_claim_reminder';
export const FIRST_WIN_GUEST_REMINDER_CAMPAIGN = 'first_win_guest_confirm_reminder';

export const FIRST_WIN_EXPRESS_SIGN_IN_CTA = 'Sign in to manage your seat';
export const FIRST_WIN_GUEST_RESEND_CTA = 'Resend confirmation email';
export const FIRST_WIN_GUEST_OPEN_EMAIL_HELPER =
  'Open your email and click the confirmation link. It expires in 48 hours.';
export const FIRST_WIN_GUEST_ADD_ANOTHER_CTA = 'Add another state';

export type FirstWinCtaKind = 'primary' | 'secondary';

export type FirstWinCta = {
  id: string;
  label: string;
  kind: FirstWinCtaKind;
  /** Absolute or app-relative href when navigation is a link. */
  href?: string;
  /** Local action id for button handlers (e.g. alerts, resend). */
  action?: 'open_alerts' | 'resend_confirm' | 'add_another_state';
};

export type ExpressSuccessActionsInput = {
  firstWinEnabled: boolean;
  claimUrl: string | null;
  hasAlertsAction: boolean;
};

export type GuestSuccessActionsInput = {
  firstWinEnabled: boolean;
  hasUpgradeSlot: boolean;
};

/**
 * Success-panel CTAs for express register.
 * Flag off preserves the legacy multi-button fork (caller still owns layout).
 */
export function getExpressSuccessActions(
  input: ExpressSuccessActionsInput,
): FirstWinCta[] {
  if (!input.firstWinEnabled) {
    const legacy: FirstWinCta[] = [];
    if (input.claimUrl) {
      legacy.push({
        id: 'claim',
        label: EXPRESS_REGISTER_SUCCESS_CLAIM_CTA,
        kind: 'primary',
        href: input.claimUrl,
      });
    }
    if (input.hasAlertsAction) {
      legacy.push({
        id: 'alerts',
        label: EXPRESS_REGISTER_SUCCESS_ALERTS_CTA,
        kind: 'primary',
        action: 'open_alerts',
      });
    }
    legacy.push({
      id: 'browse',
      label: EXPRESS_REGISTER_SUCCESS_BROWSE_CTA,
      kind: 'secondary',
      href: '/events',
    });
    return legacy;
  }

  const actions: FirstWinCta[] = [];
  if (input.claimUrl) {
    actions.push({
      id: 'claim',
      label: EXPRESS_REGISTER_SUCCESS_CLAIM_CTA,
      kind: 'primary',
      href: input.claimUrl,
    });
  } else {
    actions.push({
      id: 'sign_in',
      label: FIRST_WIN_EXPRESS_SIGN_IN_CTA,
      kind: 'primary',
      href: '/login',
    });
  }
  if (input.hasAlertsAction) {
    actions.push({
      id: 'alerts',
      label: EXPRESS_REGISTER_SUCCESS_ALERTS_CTA,
      kind: 'secondary',
      action: 'open_alerts',
    });
  }
  actions.push({
    id: 'browse',
    label: EXPRESS_REGISTER_SUCCESS_BROWSE_CTA,
    kind: 'secondary',
    href: '/events',
  });
  return actions;
}

export function getGuestSuccessActions(
  input: GuestSuccessActionsInput,
): FirstWinCta[] {
  if (!input.firstWinEnabled) {
    return [
      {
        id: 'add_another',
        label: FIRST_WIN_GUEST_ADD_ANOTHER_CTA,
        kind: 'secondary',
        action: 'add_another_state',
      },
    ];
  }

  const actions: FirstWinCta[] = [
    {
      id: 'resend',
      label: FIRST_WIN_GUEST_RESEND_CTA,
      kind: 'primary',
      action: 'resend_confirm',
    },
    {
      id: 'add_another',
      label: FIRST_WIN_GUEST_ADD_ANOTHER_CTA,
      kind: 'secondary',
      action: 'add_another_state',
    },
  ];
  if (input.hasUpgradeSlot) {
    actions.push({
      id: 'upgrade',
      label: 'Create a free account',
      kind: 'secondary',
      href: '/signup',
    });
  }
  return actions;
}

/** Pure window check for reminder eligibility (age of door complete in ms). */
export function isInFirstWinReminderWindow(
  completedAtMs: number,
  nowMs: number = Date.now(),
): boolean {
  const ageMs = nowMs - completedAtMs;
  const minMs = FIRST_WIN_REMINDER_MIN_AGE_HOURS * 60 * 60 * 1000;
  const maxMs = FIRST_WIN_REMINDER_MAX_AGE_HOURS * 60 * 60 * 1000;
  return ageMs >= minMs && ageMs <= maxMs;
}

export function expressClaimWinDeadlineMs(bookingCreatedAtMs: number): number {
  return (
    bookingCreatedAtMs + FIRST_WIN_EXPRESS_CLAIM_WINDOW_DAYS * 24 * 60 * 60 * 1000
  );
}

export function isWithinExpressClaimWinWindow(
  bookingCreatedAtMs: number,
  nowMs: number = Date.now(),
): boolean {
  return nowMs <= expressClaimWinDeadlineMs(bookingCreatedAtMs);
}

export function firstWinExpressReminderDedupeKey(userId: string | number): string {
  return `first_win_express_reminder:${String(userId)}`;
}

export function firstWinGuestReminderDedupeKey(
  email: string,
  geoStateId: number,
): string {
  return `first_win_guest_confirm_reminder:${email.trim().toLowerCase()}:${geoStateId}`;
}

export function firstWinExpressClaimWinDedupeKey(userId: string | number): string {
  return `first_win_express_claimed:${String(userId)}`;
}
